'use client';

import type {
  MarkdownPreviewProps,
  MarkdownPreviewRef,
} from '@uiw/react-markdown-preview';
import MarkdownPreviewComponent from '@uiw/react-markdown-preview/nohighlight';
import { useThemeMode } from '../theme/useThemeMode';
import { token } from '@terra/ui-styled-system';
import { baseStyles, compactOverrides } from './markdown-preview.styles';
import { memo, useEffect, useRef, useMemo } from 'react';
import { createRehypePlugins } from './utils/rehype-plugins';
import {
  rehypeObjectLinks,
  type ObjectLinkPhrase,
} from './utils/rehype-object-links';
import { remarkEscapeRawHtml } from './utils/remark-escape-raw-html';
import { repairGfmTableDelimiters } from './utils/repair-gfm-tables';
import { applyCodeFenceLineRangeStarts } from './utils/code-fence-line-range';
import './markdown-preview.css';

export type MarkdownPreviewCustomProps = Pick<
  MarkdownPreviewProps,
  'source' | 'style' | 'wrapperElement' | 'className'
> & {
  compact?: boolean;
  fontSize?: number;
  escapeRawHtml?: boolean;
  showCodeLineNumbers?: boolean;
  objectLinks?: readonly ObjectLinkPhrase[];
  components?: MarkdownPreviewProps['components'];
};

export const MarkdownPreview = memo(function MarkdownPreview({
  source,
  compact = false,
  fontSize = 13,
  escapeRawHtml = false,
  showCodeLineNumbers = false,
  objectLinks,
  components,
}: MarkdownPreviewCustomProps) {
  const { mode } = useThemeMode();
  const markdownPreviewRef = useRef<MarkdownPreviewRef>(null);
  const copyTimeoutRef = useRef<number | null>(null);

  // Combine styles based on mode
  const combinedClassName = useMemo(() => {
    return compact ? `${baseStyles} ${compactOverrides}` : baseStyles;
  }, [compact]);

  // Copy handler for code blocks
  useEffect(() => {
    const element = markdownPreviewRef.current?.mdp?.current;

    if (!element) return;

    const handleCopyClick = (event: Event) => {
      const target = event.target;

      if (!(target instanceof Element)) return;

      const copyBtn = target.closest('.copied');

      if (!(copyBtn instanceof HTMLElement)) return;

      event.preventDefault();
      event.stopPropagation();

      const text =
        copyBtn.dataset.code?.trim() ||
        copyBtn.closest('pre')?.querySelector('code')?.textContent?.trim() ||
        '';

      if (!text) return;

      navigator.clipboard
        .writeText(text)
        .then(() => {
          copyBtn.classList.add('active');

          if (copyTimeoutRef.current) {
            clearTimeout(copyTimeoutRef.current);
          }

          copyTimeoutRef.current = window.setTimeout(() => {
            if (copyBtn.isConnected) {
              copyBtn.classList.remove('active');
            }

            copyTimeoutRef.current = null;
          }, 1500);
        })
        .catch((err) => {
          console.error('Failed to copy:', err);
        });
    };

    element.addEventListener('click', handleCopyClick, true);

    return () => {
      element.removeEventListener('click', handleCopyClick, true);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    };
  }, []);

  // CSS custom properties for dynamic sizing
  const customStyles = useMemo(
    () => ({
      fontFamily: 'inherit',
      backgroundColor: 'transparent',
      color: 'inherit',
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      '--markdown-font-size': `${fontSize}px`,
      '--markdown-font-size-small': `${fontSize - 1}px`,
      '--markdown-font-size-code': `${fontSize - 2}px`,
      '--markdown-heading-font-weight': compact
        ? '500'
        : token('fontWeights.bold'),
      '--markdown-subheading-font-weight': compact
        ? '500'
        : token('fontWeights.semibold'),
      '--markdown-strong-font-weight': compact ? '500' : '600',
      '--color-canvas-default': token('colors.bg.subtle'),
      '--color-canvas-subtle': token('colors.bg.subtle'),
      '--color-border-default': token('colors.border.neutral.muted'),
      '--color-border-muted': token('colors.border.neutral.muted'),
    }),
    [compact, fontSize],
  );

  const repairedSource = useMemo(
    () => repairGfmTableDelimiters(source ?? ''),
    [source],
  );
  const rehypePlugins = useMemo((): MarkdownPreviewProps['rehypePlugins'] => {
    const plugins = createRehypePlugins() ?? [];

    if (!objectLinks?.length) return plugins;

    return [...plugins, [rehypeObjectLinks, { links: objectLinks }]];
  }, [objectLinks]);
  const remarkPlugins = useMemo(
    () => (escapeRawHtml ? [remarkEscapeRawHtml] : undefined),
    [escapeRawHtml],
  );

  const wrapperElement = useMemo(
    () => ({ 'data-color-mode': mode as 'light' | 'dark' }),
    [mode],
  );

  useEffect(() => {
    const element = markdownPreviewRef.current?.mdp?.current;

    if (!element || !showCodeLineNumbers) {
      return;
    }

    applyCodeFenceLineRangeStarts({ root: element });
  }, [repairedSource, showCodeLineNumbers]);

  return (
    <MarkdownPreviewComponent
      ref={markdownPreviewRef}
      className={combinedClassName}
      source={repairedSource}
      style={customStyles as React.CSSProperties}
      wrapperElement={wrapperElement}
      rehypePlugins={rehypePlugins}
      remarkPlugins={remarkPlugins}
      components={components}
    />
  );
});
